"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Wrench, ShoppingCart, Settings, Shield, Calendar } from "@/lib/icons";

interface ServiceItem {
  id: string;
  title: string;
  description: string;
  icon: typeof Wrench;
  details: string;
  quote?: string;
}

const services: ServiceItem[] = [
  {
    id: "rental",
    title: "Laundry Equipment Rental & Sales",
    description: "We offer rental contracts with full service cover; ideal for busy environments with high laundry throughput.",
    icon: ShoppingCart,
    details: "Flexible, reliable, and cost-effective. Washers and Dryers from Girbau, Speed Queen, IPSO, Heubsch, Miele, Primus. Both electric and gas models. Free consultation & site assessment.",
  },
  {
    id: "installation",
    title: "Installation & Commissioning",
    description: "From light to heavy-duty installations, we manage everything including plumbing, electrics, gas connections, and commissioning.",
    icon: Settings,
    details: "CAD designs available for planning and compliance.",
  },
  {
    id: "maintenance",
    title: "Service, Maintenance & Repairs",
    description: "We respond to emergency breakdowns as well as delivering planned appliance servicing.",
    icon: Wrench,
    details: "We repair laundry machines supplied by us or others. Our reputation is built on speed, reliability, and logic-driven problem solving.",
    quote: "No error codes? No problem. Our experience goes deeper.",
  },
  {
    id: "gas",
    title: "Gas Certification & Safety Compliance",
    description: "Fully Gas Safe registered for commercial laundry gas appliances.",
    icon: Shield,
    details: "We ensure every dryer and gas-fired machine complies with current UK regulations.",
  },
  {
    id: "consultancy",
    title: "Consultancy & CAD Design",
    description: "Need help designing or expanding your laundry room? We provide technical consultancy.",
    icon: Calendar,
    details: "Including bespoke CAD layouts and compliance checks to meet CQC, fire safety and HSE standards.",
  },
];

export function ServicesSection() {
  return (
    <section 
      id="services" 
      aria-labelledby="services-heading"
      className="py-24 px-4 bg-gradient-to-b from-blue-900/[0.06] via-blue-50/15 to-white relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_0%_0%,_rgba(59,130,246,0.08),_transparent_50%)]" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_100%_100%,_rgba(59,130,246,0.06),_transparent_40%)]" />
        <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-blue-100/30 rounded-full blur-3xl -translate-x-1/2" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-50/25 rounded-full blur-3xl translate-x-1/2" />
        <div className="absolute top-1/4 right-1/4 w-[300px] h-[300px] bg-sky-100/20 rounded-full blur-2xl" />
        <div className="absolute bottom-1/3 left-1/4 w-[250px] h-[250px] bg-blue-50/20 rounded-full blur-2xl" />
      </div>
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-blue-600 font-semibold text-sm sm:text-base uppercase tracking-wider mb-4 sm:mb-4 block" aria-hidden="true">Our Services</span>
          <h2 id="services-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 sm:mb-6">
            Our Services
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed" role="doc-subtitle">
            Comprehensive commercial laundry solutions from rental and sales to emergency repairs. 
            Gas Safe certified and working with leading manufacturers.
          </p>
        </div>

        {/* Desktop Carousel */}
        <div className="hidden md:block">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
            aria-label="Services carousel"
          >
            <CarouselContent className="-ml-4">
              {services.map((service, index) => (
                <CarouselItem 
                  key={service.id} 
                  className="pl-4 md:basis-1/2 lg:basis-1/3"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${services.length}`}
                >
                  <Card className="h-full group hover:shadow-2xl transition-all duration-300 border-gray-200/50 hover:border-blue-200 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
                    <CardHeader className="relative">
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
                        <service.icon className="h-10 w-10 text-blue-600" aria-hidden="true" />
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-semibold text-gray-900" role="heading" aria-level={3}>{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base sm:text-lg mb-4 text-gray-700 leading-relaxed">
                        {service.description}
                      </CardDescription>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{service.details}</p>
                      {service.quote && (
                        <p className="text-sm sm:text-base text-blue-600 font-medium italic mt-4">
                          &quot;{service.quote}&quot;
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious aria-label="Previous slide" />
            <CarouselNext aria-label="Next slide" />
          </Carousel>
        </div>

        {/* Mobile Vertical List */}
        <div 
          className="md:hidden space-y-6"
          role="list"
          aria-label="Services list"
        >
          {services.map((service) => (
            <Card 
              key={service.id} 
              className="group hover:shadow-lg transition-all duration-300 border-gray-200/50 hover:border-blue-200"
              role="listitem"
            >
              <CardHeader className="relative">
                <div className="flex items-center gap-5">
                  <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="h-8 w-8 text-blue-600" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl font-semibold text-gray-900" role="heading" aria-level={3}>{service.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base sm:text-lg mb-4 text-gray-700 leading-relaxed">
                  {service.description}
                </CardDescription>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{service.details}</p>
                {service.quote && (
                  <p className="text-sm sm:text-base text-blue-600 font-medium italic mt-4">
                    &quot;{service.quote}&quot;
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
